<script setup lang="ts">
import ContainerDialog from '@/components/rc/containers/ContainerDialog.vue'
import AssignContainerToShipmentDialog from '@/components/rc/shipments/AssignContainerToShipmentDialog.vue'
import ContainersManagementModal from '@/components/rc/shipments/ContainersManagementModal.vue'
import ShipmentBreakdownCard from '@/components/rc/shipments/ShipmentBreakdownCard.vue'
import ShipmentDialog from '@/components/rc/shipments/ShipmentDialog.vue'
import type { IFragmentFilters } from '@/types/shared/filters'
import { getWorkOrderDashboardShipments, getWorkOrderDashboardSummary } from '@/services/rc/work-orders.service'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES } from '@/types/shared/pagination'
import { ShipmentType, type IShipment } from '@/types/rc/shipments'
import type { IWorkOrderDashboardShipment, IWorkOrderDashboardShipmentContainer } from '@/types/rc/work-orders'
import { Search } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getAllCarriers, getShipmentDetails } from '@/services/rc/shipments.service'
import { useWorkOrder } from '@/composables/rc/work-order'

const router = useRouter()

const activeTab = useRouteQuery<ShipmentType>('tab', ShipmentType.Inbound)

const props = defineProps<{ number: string }>()

// Local state
const targetKind = ref<ShipmentType>(activeTab.value)
const activeInboundItems = ref<IWorkOrderDashboardShipment['id'][]>([])
const activeOutboundItems = ref<IWorkOrderDashboardShipment['id'][]>([])
const fragment = ref<string | undefined>(undefined)

// Dialogs
const containerDialogOpen = ref(false)
const shipmentDialogOpen = ref(false)
const addContainersToShipmentDialogOpen = ref(false)
const assignContainersToShipmentDialogOpen = ref(false)

// Edit - Edit shipment
const editShipmentTarget = ref<IShipment>()
const editContainerTarget = ref<IWorkOrderDashboardShipmentContainer>()

// Assignment - Assign unassigned containers to shipment
const containersSelected = ref<IWorkOrderDashboardShipmentContainer[]>()

const filters = reactive<IFragmentFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  fragment: undefined,
})

// Computed

const containersSelectedIds = computed(() => {
  return containersSelected.value?.map((container) => {
    return {
      id: container.id,
      number: container.number,
    }
  })
})

// Server bindings

const { isManageableByCurrentUser } = useWorkOrder(props.number)

const { data: carrierOptions } = useQuery({ queryKey: ['carrier-options'], queryFn: getAllCarriers })

const {
  data: shipmentsData,
  error,
  isError,
  isFetching,
  refetch,
} = useQuery({
  queryKey: ['work-order-logistics', props.number, filters],
  queryFn: () =>
    getWorkOrderDashboardShipments(props.number, filters)
      // Send the unassigned shipment to the top of the list
      .then((res) => res.sort((c1: any, c2: any) => (c1.id === 'Unassigned' ? -1 : c2.id === 'Unassigned' ? 1 : 0))),
  enabled: !!props.number,
})

const inboundShipments = computed(
  () => shipmentsData.value?.filter((shipment: any) => shipment.type === ShipmentType.Inbound) ?? []
)
const outboundShipments = computed(
  () => shipmentsData.value?.filter((shipment: any) => shipment.type === ShipmentType.Outbound) ?? []
)

// Fetch the customer from the work order. Since we have to get through the summary
// to get to this tab, we can safely assume the summary is already cached.
const { data: workOrderDashboardSummary } = useQuery({
  queryKey: ['work-order-dashboard-summary', props.number],
  queryFn: () => getWorkOrderDashboardSummary(props.number),
})

const onContainersAssigned = () => {
  containersSelected.value = []
}

// Event handlers

const onSearchInput = useDebounceFn((value: string) => {
  filters.fragment = value.trim()
})

const onNavToContainerView = (containerID: number) => {
  router.push({ name: 'rc-logistics-container-details', params: { containerID } })
}

const onAddContainersToShipment = async (shipmentId: string, kind: ShipmentType) => {
  targetKind.value = kind
  if (shipmentId == 'Unassigned') {
    editShipmentTarget.value = undefined
    containerDialogOpen.value = true
  } else {
    editShipmentTarget.value = await getShipmentDetails(parseInt(shipmentId))
    addContainersToShipmentDialogOpen.value = true
  }
}

const onAssignContainers = (containers: IWorkOrderDashboardShipmentContainer[], kind: ShipmentType) => {
  containersSelected.value = containers
  targetKind.value = kind
  assignContainersToShipmentDialogOpen.value = true
}

const onEditContainer = (container: IWorkOrderDashboardShipmentContainer) => {
  editContainerTarget.value = container
  containerDialogOpen.value = true
}

/**
 * Resets the arguments passed into the dialogs
 */
const onDialogClosed = () => {
  editShipmentTarget.value = undefined
  editContainerTarget.value = undefined
  containersSelected.value = []
}

const onAddInboundShipment = () => {
  targetKind.value = ShipmentType.Inbound
  shipmentDialogOpen.value = true
}

const onAddOutboundShipment = () => {
  targetKind.value = ShipmentType.Outbound
  shipmentDialogOpen.value = true
}
</script>

<template>
  <ContainerDialog
    v-model="containerDialogOpen"
    :work-order-number="props.number"
    :container-id="editContainerTarget?.id"
    :kind="targetKind"
    @closed="onDialogClosed"
  />
  <ShipmentDialog
    v-if="carrierOptions"
    v-model:open="shipmentDialogOpen"
    :work-order-number="props.number"
    :shipment="editShipmentTarget"
    :carriers-options="carrierOptions"
    :kind="targetKind"
    @closed="onDialogClosed"
  />
  <ContainersManagementModal
    v-model="addContainersToShipmentDialogOpen"
    :work-order="number"
    :customer="workOrderDashboardSummary?.work_order_information.customer.name"
    :shipment="editShipmentTarget"
    @closed="onDialogClosed"
  />
  <AssignContainerToShipmentDialog
    v-if="assignContainersToShipmentDialogOpen && containersSelectedIds"
    v-model="assignContainersToShipmentDialogOpen"
    :containers="containersSelectedIds"
    :customer="workOrderDashboardSummary?.work_order_information.customer.id"
    :container-shipment-type="targetKind"
    @containers-assigned="onContainersAssigned"
  />

  <rl-error-result v-if="isError && !isFetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <div v-else class="flex flex-col gap-spacing-lg">
    <!-- Controls and buttons -->
    <div class="flex justify-end items-center">
      <el-input
        v-model="fragment"
        v-loading="isFetching"
        class="w-80"
        placeholder="Shipment or Container"
        clearable
        :prefix-icon="Search"
        @input="onSearchInput"
      />
    </div>

    <!-- Shipment cards -->
    <div v-if="shipmentsData?.length" class="flex w-full overflow-auto justify-between gap-4">
      <el-card v-loading="isFetching" class="w-full h-max">
        <template #header>Inbound</template>
        <el-link v-if="isManageableByCurrentUser" class="text-text-brand w-max mb-4" @click="onAddInboundShipment"
          >+ Add shipment</el-link
        >
        <el-collapse v-model="activeInboundItems" class="flex flex-col gap-spacing-md border-none">
          <ShipmentBreakdownCard
            v-for="shipment in inboundShipments"
            :key="shipment.id"
            :expanded="activeInboundItems.includes(shipment.id)"
            :shipment="shipment"
            :editable="isManageableByCurrentUser"
            @container-details="onNavToContainerView"
            @edit-container="onEditContainer"
            @add-containers="onAddContainersToShipment"
            @assign-containers="onAssignContainers"
          />
        </el-collapse>
      </el-card>
      <el-card v-loading="isFetching" class="w-full h-max">
        <template #header>Outbound</template>
        <el-link v-if="isManageableByCurrentUser" class="text-text-brand w-max mb-4" @click="onAddOutboundShipment"
          >+ Add shipment</el-link
        >
        <el-collapse v-model="activeOutboundItems" class="flex flex-col gap-spacing-md border-none">
          <ShipmentBreakdownCard
            v-for="shipment in outboundShipments"
            :key="shipment.id"
            :expanded="activeOutboundItems.includes(shipment.id)"
            :shipment="shipment"
            :editable="isManageableByCurrentUser"
            @container-details="onNavToContainerView"
            @edit-container="onEditContainer"
            @add-containers="onAddContainersToShipment"
            @assign-containers="onAssignContainers"
          />
        </el-collapse>
      </el-card>
    </div>

    <!-- Empty dataset -->
    <template v-if="!isFetching && shipmentsData?.length == 0">
      <rl-empty-dataset title="Oops, nothing in here" subtitle="No data was returned for your search" />
    </template>
  </div>
</template>

<style scoped lang="scss">
:deep(.el-button) {
  margin-left: 0;
}
</style>
